import { useEffect, useRef, useState } from "react";
import { HeroContent } from "./HeroContent";
import { MobilePortal } from "./MobilePortal";
import { LoopVideo } from "./LoopVideo";
import { UNDERWATER_DIM } from "./underwater";
import { StardustDissolve } from "./StardustDissolve";
import { useSiteAudio } from "./SiteAudio";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FEATURED_SONG, LOGO } from "@/data/site";

// Thin switcher — mobile gets an entirely separate tap-to-enter component
// (MobilePortal), desktop keeps its scroll-scrub. Kept as two fully separate
// component trees (not an isMobile branch inside one component) so the
// desktop implementation's hooks are never conditionally skipped across a
// breakpoint-crossing re-render.
export const CinematicExperience = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobilePortal /> : <DesktopExperience />;
};

// Portal loop — Trevor's composite (the real IMG_1555 on the cosmic-beach
// plate) with the sky animated in Motionleap: Milky Way drifting toward her,
// light twinkle, a few pink fireflies. Re-encoded at half speed (the slowest
// Motionleap setting still read as too fast). Wordmark is layered in code.
// Sky held still on frame 0 (the dive's first frame); only the sea moves,
// so the galaxy never drifts out of line before the dive takes over. Her spot
// is baked in as the empty beach (the dive's first frame): she's only ever
// the cut-out photo layer on top, so she can't flash in from the background.
//
// Every video is tagged sRGB (transfer 13), the same as the WebP stills.
// Tagged BT.709, Safari and Chrome on a Mac show video lighter in the
// shadows than the identical pixels in an image, so the still-to-video swap
// went dark-to-light and the whole scene shifted whenever her fade changed
// how the page was composited.
const PORTAL_LOOP = "/jennrock-portal-loop-desktop-v4.mp4";
const PORTAL_LOOP_POSTER = "/jennrock-portal-loop-desktop-v4.webp";
// The Portal still with her in it: her photo layer and the dust colours.
const PORTAL_POSTER = "/jennrock-portal-loop-desktop-v2.webp";

// Landing loop — underwater, looking up at the galaxy through the surface
// (the "deep & mysterious ... what lives beneath" chapter of her world).
const LANDING_LOOP = "/jennrock-landing-loop-desktop-v2.mp4";
const LANDING_POSTER = "/jennrock-landing-loop-desktop-v2.webp";

// Transition — the 8s Seedance dive: frame 0 is the Magic-Eraser clean beach
// (her removed, same framing as the Portal loop), then it pushes over the
// sand to the shoreline, dips under at ~5.5s, and ends underwater looking up
// at the galaxy, matching the Landing loop's first frame. Re-encoded H.264
// as all keyframes (every frame an I-frame), like Sierra's, so currentTime
// scrubbing is a single-frame decode per seek.
// 60fps (interpolated 24 -> 60, motion-compensated) for Sierra-smooth slow
// scrubbing. 1600x900, every frame a keyframe, encoded for fast decoding
// (CAVLC, no deblock): ~1.9 ms per seek in software vs ~5 ms for the old
// keyframe-every-2nd-frame file, at SSIM 0.997 to it. 9.3 MB.
const TRANSITION = "/jennrock-dive-desktop-v7.mp4";
// Lighter dive for slower or smaller laptops (e.g. a 2017 MacBook Air on old
// Chrome, where each seek is costly): 1280x720 at 30fps, same all-keyframe
// fast-decode encode, 4.2 MB. On a <=1600px-wide screen it looks the same.
const TRANSITION_LITE = "/jennrock-dive-desktop-lite.mp4";
const pickTransition = () => {
  try {
    const cores = navigator.hardwareConcurrency || 8;
    const px = window.screen.width * (window.devicePixelRatio || 1);
    return cores <= 4 || px <= 1600 ? TRANSITION_LITE : TRANSITION;
  } catch (e) {
    return TRANSITION;
  }
};
const TRANSITION_POSTER = "/jennrock-dive-desktop-v5.webp";
// Her silhouette (Portal art vs the clean start frame) — where the dust spawns.
const HER_MASK = "/jennrock-her-silhouette-desktop.png";
// That still, cut to HER_MASK as a transparent WebP: her photo layer.
const HER_CUTOUT = "/jennrock-her-cutout-desktop.webp";
const DUST_POSITION = [0.5, 0.5];

// The first nudge of scroll past this point sets her dissolve off (it runs on
// its own clock, not the scroll); scrolling back to the very top restores her.
const DISSOLVE_AT = 0.004;
// Her fade under the stardust: starts a beat after the dust so the first
// motes are visible while she's still there. Kept short (75% quicker than the
// original 1.5s + 0.2s) so a fast swipe never carries her into the Landing.
const PORTAL_FADE = "opacity 0.375s cubic-bezier(0.4,0,0.8,0.6) 0.05s";
// Her entrance on page load (and when scrolling back to the top): a slow
// fade up onto the empty beach, once her photo and masks are decoded, so
// she never pops in half-loaded.
const HER_ENTER = "opacity 1.5s cubic-bezier(0.45,0,0.55,1) 0.3s";

// Scroll range (0..1 of the pinned section) the 8s transition is scrubbed
// across — exactly the sierra-marin-website range: it starts moving on the
// very first scroll, while her stardust dissolve plays on top.
const TRANSITION_START = 0;
const TRANSITION_END = 0.85;

// Seedance re-rendered the first frame ~3% zoomed out and nudged up/left vs
// the Portal art. That registration is baked into the file itself
// (scale 1.0325, +1.04% x, +1.48% y, measured by a full-res edge match),
// held through the beach and eased out deep underwater (55-80% of the dive),
// so frame 0 is pixel-for-pixel the Portal framing and the last frame is the
// untouched Landing match. No runtime transform: it plays full-bleed like
// the loops.
//
// It's also stabilized: Seedance's camera tilts as it pushes forward, so the
// horizon slid up ~38px over the beach section the moment you scrolled. A
// per-frame counter-shift (from a measured drift curve, with a gentle zoom
// for the margin) keeps the mountains/horizon within ~3px through the beach,
// released together with the registration deep underwater.
//
// v5: frame 0 is built from the Portal loop's own first frame (her area
// filled with the dive's clean beach), blended into the Seedance footage
// over the first 0.4s. The blend is done in native YUV, so there's no colour
// conversion: the instant cut from the Portal is pixel-identical (luma diff
// ~0.4, compression noise only) instead of a re-drawn texture. Posters are
// extracted as BT.709, matching how browsers display these files.

// Pinned Portal -> Transition -> Landing scroll-scrub, desktop only. A tall
// section holds a sticky 100svh viewport; scroll progress `p` (0..1) drives
// crossfades via seg(a,b) envelopes, and a RAF loop eases the transition
// video's currentTime toward the scroll position — the same mechanism as the
// sierra-marin-website template.
//
// Her real photo is only ever in the Portal loop. The transition video sits
// directly underneath it, parked on its clean first frame. On the first
// scroll she breaks into stardust (StardustDissolve) while the Portal layer
// fades, so she leaves the beach in about a second and the AI-rendered
// frames never show her.
const DesktopExperience = () => {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const transitionRef = useRef(null);
  const targetTimeRef = useRef(0);
  const durationRef = useRef(0);
  const lastSeekRef = useRef(0);
  const [transitionSrc] = useState(pickTransition);
  const audioRef = useSiteAudio();
  const audioStartedRef = useRef(false);
  const [p, setP] = useState(0);
  const [dissolved, setDissolved] = useState(false);
  // The dive downloads first. The Portal and Landing loops show their poster
  // stills (identical first frames) until it can play through, or 8s pass.
  const [loopsOn, setLoopsOn] = useState(false);
  const [herReady, setHerReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const decode = (src) => {
      const img = new Image();
      img.src = src;
      return img.decode().catch(() => {});
    };
    const go = () => alive && setHerReady(true);
    Promise.all([HER_CUTOUT, PORTAL_POSTER, HER_MASK].map(decode)).then(go);
    const t = setTimeout(go, 4000);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);
  useEffect(() => {
    const v = transitionRef.current;
    const go = () => setLoopsOn(true);
    if (!v || v.readyState >= 4) return go();
    v.addEventListener("canplaythrough", go, { once: true });
    const t = setTimeout(go, 8000);
    return () => {
      v.removeEventListener("canplaythrough", go);
      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const vh = stickyRef.current ? stickyRef.current.clientHeight : window.innerHeight;
      const total = rect.height - vh;
      const prog = Math.min(1, Math.max(0, -rect.top / total));
      setP(prog);
      // Latched: once she's dissolved she only comes back at the very top, so
      // small scroll wiggles near the start can't flicker her back in.
      setDissolved((was) => (prog > DISSOLVE_AT ? true : prog <= 0 ? false : was));

      const tProg = Math.min(1, Math.max(0, (prog - TRANSITION_START) / (TRANSITION_END - TRANSITION_START)));
      targetTimeRef.current = tProg * durationRef.current;

      // Song clip (FEATURED_SONG.audioSrc): try to start as she dissolves.
      // Browsers usually block sound until a click/tap/key, so this often
      // fails silently, and the speaker button (SoundToggle) starts it on
      // the visitor's first interaction instead.
      if (!audioStartedRef.current && prog > 0.02 && audioRef.current) {
        audioStartedRef.current = true;
        audioRef.current.play().catch(() => {});
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [audioRef]);

  // Duration is read live from the file, so a re-cut of any length just works.
  useEffect(() => {
    const v = transitionRef.current;
    if (!v) return;
    const onMeta = () => {
      durationRef.current = v.duration || 0;
      window.dispatchEvent(new Event("scroll"));
    };
    if (v.readyState >= 1) onMeta();
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, []);

  // Ease currentTime toward the scroll target every frame (template easing).
  //
  // Slow-machine fallback: on some older laptops (e.g. a 2017 MacBook on
  // Mojave's last Chrome) seeks are so slow, or never finish painting, that
  // the dive shows nothing until the scroll stops and then appears to skip to
  // the end. Seek times are measured; if they're slow (or one hangs), the dive
  // switches to playing forward toward the scroll position at an adjusted
  // speed instead, which every machine can do smoothly. `?slowscrub` forces it.
  useEffect(() => {
    const v = transitionRef.current;
    if (!v) return;
    let raf;
    let seekAt = 0;
    const times = [];
    const slow = { on: /[?&]slowscrub\b/.test(window.location.search) };
    const onSeeked = () => {
      if (seekAt) times.push(performance.now() - seekAt);
      seekAt = 0;
      if (times.length > 6) times.shift();
      if (times.length >= 4) {
        const sorted = [...times].sort((a, b) => a - b);
        if (sorted[sorted.length >> 1] > 90) slow.on = true;
      }
    };
    v.addEventListener("seeked", onSeeked);

    // Some browsers won't paint a frame from a seek until the video has
    // played once, so play it for an instant (muted, hidden under the
    // Portal) as soon as it can.
    let primed = false;
    const prime = () => {
      if (primed) return;
      primed = true;
      const p = v.play();
      if (p && p.then) p.then(() => v.pause()).catch(() => {});
      else v.pause();
    };
    if (v.readyState >= 2) prime();
    else v.addEventListener("loadeddata", prime, { once: true });

    const tick = () => {
      if (durationRef.current) {
        const cur = v.currentTime;
        const target = targetTimeRef.current;
        const diff = target - cur;
        // A seek that's been in flight for over a quarter second means this
        // machine can't scrub by seeking.
        if (seekAt && performance.now() - seekAt > 250) slow.on = true;

        if (slow.on) {
          if (diff > 0.04) {
            v.playbackRate = Math.min(4, Math.max(0.75, diff * 3));
            if (v.paused) v.play().catch(() => {});
          } else if (diff < -0.25) {
            // Scrolling back up: playing in reverse isn't possible, so jump.
            if (!v.paused) v.pause();
            if (!v.seeking) {
              seekAt = 0;
              try {
                v.currentTime = target;
              } catch (e) {}
            }
          } else if (!v.paused) {
            v.pause();
          }
        } else if (Math.abs(diff) > 0.0015 && !v.seeking) {
          // Follow tiny moves (Sierra's threshold), but never stack a new
          // seek on one that's still in flight: each new currentTime cancels
          // the previous seek, so during a scroll no frame ever got painted.
          // 20% of the way per 60fps frame, scaled by the time since the last
          // seek, so a slower machine takes even, bigger steps.
          const now = performance.now();
          const gap = now - lastSeekRef.current;
          const dt = gap > 100 ? 16.7 : Math.max(16.7, gap);
          lastSeekRef.current = now;
          const k = 1 - Math.pow(0.8, dt / 16.7);
          seekAt = now;
          try {
            v.currentTime = cur + diff * k;
          } catch (e) {}
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("loadeddata", prime);
    };
  }, []);

  const seg = (a, b) => Math.min(1, Math.max(0, (p - a) / (b - a)));

  const portalTextOp = 1 - seg(0, 0.03);
  // Sierra's envelope: the galaxy loop melts into the dive over the first
  // 15% of the scroll, so the loop's moving sky never visibly jumps.
  const portalLoopOp = 1 - seg(0, 0.15);
  const transitionOp = 1 - seg(0.85, 0.93);

  const landingOp = seg(0.78, 0.9);
  const heroContentOp = seg(0.86, 0.95);
  const dimOp = UNDERWATER_DIM * seg(0.6, 0.88);
  const pe = (o) => (o > 0.5 ? "auto" : "none");

  return (
    <section id="experience" ref={sectionRef} className="relative" style={{ height: "280vh" }}>
      <div ref={stickyRef} className="sticky top-0 h-screen w-full overflow-hidden bg-[var(--sm-black)]" style={{ height: "100svh" }}>
        {/* Each fading layer keeps its own compositing layer (will-change), so
            her fade starting or ending never changes how the video is drawn. */}
        {/* Layer 0: Landing loop — underwater, looking up at the galaxy. */}
        <div className="absolute inset-0" style={{ opacity: landingOp, willChange: "opacity" }}>
          <LoopVideo
            src={LANDING_LOOP}
            poster={LANDING_POSTER}
            defer={!loopsOn}
            testId="landing-loop"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Layer 1: Transition — scrubbed by scroll. Its first frame is a
            separate image underneath rather than the video's poster: some
            browsers keep showing a poster until the video plays. */}
        <img
          src={TRANSITION_POSTER}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ opacity: transitionOp }}
        />
        <video
          ref={transitionRef}
          data-testid="transition-video"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: transitionOp, willChange: "opacity" }}
          src={transitionSrc}
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden
        />

        {/* Underwater dim — over the dive's tail + Landing loop. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-black" style={{ opacity: dimOp, willChange: "opacity" }} />

        {/* Layer 2: Portal loop, without her (the masked img below is her).
            Crossfades into the dive by scroll position. */}
        <div className="absolute inset-0" style={{ opacity: portalLoopOp, willChange: "opacity" }}>
          <LoopVideo
            src={PORTAL_LOOP}
            poster={PORTAL_LOOP_POSTER}
            alt="Jenn Rock on a beach under the Milky Way"
            defer={!loopsOn}
            testId="portal-loop"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Her, alone: the Portal still pre-cut to her silhouette (real alpha,
            no CSS mask, so nothing can flash unmasked while she fades). She
            fades up once decoded, and is the only thing that fades under the dust. */}
        <img
          src={HER_CUTOUT}
          alt=""
          aria-hidden
          decoding="sync"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{
            opacity: dissolved || !herReady ? 0 : 1,
            willChange: "opacity",
            transition: dissolved ? PORTAL_FADE : HER_ENTER,
          }}
        />

        <StardustDissolve active={dissolved} stagger={0.25} mask={HER_MASK} poster={PORTAL_POSTER} objectPosition={DUST_POSITION} />

        {/* Portal text: wordmark + "Scroll to Enter", in the dark sky upper-left. */}
        <div className="absolute inset-0 z-20" style={{ opacity: portalTextOp, pointerEvents: pe(portalTextOp) }}>
          <div className="absolute flex -translate-x-1/2 flex-col items-center" style={{ left: "22%", top: "24%" }}>
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[220%] w-[150%] -translate-x-1/2 -translate-y-1/2 rounded-[50%]"
              style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0) 76%)", filter: "blur(12px)" }}
            />
            <img
              src={LOGO.wordmark}
              alt="Jenn Rock"
              className="w-[24rem] max-w-[30vw]"
              style={{ filter: "drop-shadow(0 0 18px rgba(255,46,151,0.35)) drop-shadow(0 2px 14px rgba(0,0,0,0.8))" }}
            />
            <span className="font-accent mt-12 text-sm uppercase tracking-[0.32em] text-[var(--sm-gold-light)] text-legible">
              Scroll to Enter
            </span>
            <div className="mt-5 flex h-9 w-5 items-end justify-center rounded-full border border-white/30 p-1">
              <span className="block h-2 w-1 rounded-full bg-[var(--sm-gold)]" style={{ animation: "scroll-cue 1.8s ease-in-out infinite" }} />
            </div>
          </div>
        </div>

        {/* Landing content — logo, tagline, bio, My Story; centered. */}
        <div className="absolute inset-0 z-30" style={{ opacity: heroContentOp, pointerEvents: pe(heroContentOp) }}>
          <HeroContent active={heroContentOp > 0.3} />
        </div>


        {/* Progress hairline */}
        <div
          className="absolute bottom-0 left-0 z-40 h-[2px] bg-gradient-to-r from-[var(--sm-gold-accent)] to-[var(--sm-gold-light)]"
          style={{ width: `${p * 100}%`, opacity: 0.7 }}
        />
      </div>
    </section>
  );
};
